/**
 * The appointment detail dialog (#847, D-F) — the "source" view behind every
 * appointment on `/calendar`, with edit, delete and the status changes.
 *
 * Moved here from `components/medical/RecordDetailDialogs.tsx` with no string
 * change (#2109 R3 L0), so the Calendar lane (L2) and the Health lane (L3)
 * own disjoint files. It still renders inside the Health record shell
 * (`DetailShell` / `DetailField`, exported from `RecordDetailDialogs`).
 *
 * Destructive actions confirm in-app through the shared <ConfirmActionDialog>
 * (no browser confirm(), D-C).
 */
import { useState } from 'react';
import { Pencil, Stethoscope, Trash2 } from 'lucide-react';
import { ConfirmActionDialog } from '@/components/ui/ConfirmActionDialog';
import { Button } from '@/components/ui/button';
import { StatusChip, type ChipTone } from '@/components/editorial';
import { DetailField, DetailShell } from '@/components/medical/RecordDetailDialogs';
import { useTranslation } from 'react-i18next';
import { labelFor } from '@/i18n/labels';
import { formatPreset, formatTime, formatWeekday } from '@/i18n/intl';
import { APPT_TYPE_LABEL_KEY } from '@/lib/calendar-display';
import type { MedicalAppointment } from '@/types/api';

// ── Appointment detail ────────────────────────────────────────────────────

interface AppointmentDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appt: MedicalAppointment;
  questionCount?: number;
  onEdit: (appt: MedicalAppointment) => void;
  onSetStatus: (appt: MedicalAppointment, status: MedicalAppointment['status']) => void;
  onDelete: (appt: MedicalAppointment) => void;
  isMutating?: boolean;
}

/** Status names, a key map resolved at render (#2109 R3-B4). */
export const APPT_STATUS_LABEL_KEY = {
  scheduled: 'calendar:apptStatus.scheduled',
  attended:  'calendar:apptStatus.attended',
  missed:    'calendar:apptStatus.missed',
  cancelled: 'calendar:apptStatus.cancelled',
} as const satisfies Record<MedicalAppointment['status'], string>;

const APPT_STATUS_TONE: Record<MedicalAppointment['status'], ChipTone> = {
  scheduled: 'neutral',
  attended:  'ok',
  // A missed appointment is a thing to reschedule, not an emergency.
  missed:    'warn',
  cancelled: 'neutral',
};

export function AppointmentDetailDialog({
  open,
  onOpenChange,
  appt,
  questionCount,
  onEdit,
  onSetStatus,
  onDelete,
  isMutating,
}: AppointmentDetailDialogProps) {
  const { t } = useTranslation(['calendar', 'common']);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const scheduled = appt.status === 'scheduled';
  // "Tue Sep 22, 2026 · 9:05 AM" through Intl (R3-B5); an unparseable date reads "—", as safeFormat did.
  const at = new Date(appt.date_time);
  const whenParams = Number.isNaN(at.getTime())
    ? null
    : { weekday: formatWeekday(at), date: formatPreset(at, 'monthDayYear'), time: formatTime(at) };
  const when = !whenParams
    ? (appt.duration_mins ? t('detail.unknownWhenWithDuration', { minutes: appt.duration_mins }) : '—')
    : appt.duration_mins
      ? t('detail.whenWithDuration', { ...whenParams, minutes: appt.duration_mins })
      : t('detail.when', whenParams);

  return (
    <>
      <DetailShell
        open={open}
        onOpenChange={onOpenChange}
        kicker={t('detail.kicker')}
        title={appt.title}
        icon={Stethoscope}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              className="mr-auto text-destructive hover:text-destructive"
              disabled={isMutating}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t('common:actions.delete')}
            </Button>
            {scheduled ? (
              <>
                {/* "Cancel appointment" is the primary remove affordance —
                    recoverable, unlike delete (D-C). */}
                <Button variant="outline" size="sm" disabled={isMutating} onClick={() => onSetStatus(appt, 'cancelled')}>
                  {t('detail.cancelAppointment')}
                </Button>
                <Button variant="outline" size="sm" disabled={isMutating} onClick={() => onSetStatus(appt, 'attended')}>
                  {t('detail.markAttended')}
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" disabled={isMutating} onClick={() => onSetStatus(appt, 'scheduled')}>
                {t('detail.backToScheduled')}
              </Button>
            )}
            <Button variant="brand" size="sm" disabled={isMutating} onClick={() => onEdit(appt)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t('common:actions.edit')}
            </Button>
          </>
        }
      >
        <DetailField label={t('detail.field.status')}>
          <StatusChip tone={APPT_STATUS_TONE[appt.status]}>{labelFor(APPT_STATUS_LABEL_KEY, appt.status, t)}</StatusChip>
        </DetailField>
        <DetailField label={t('detail.field.type')}>{labelFor(APPT_TYPE_LABEL_KEY, appt.appointment_type, t)}</DetailField>
        <DetailField label={t('detail.field.when')}>
          {when}
        </DetailField>
        {appt.provider && <DetailField label={t('detail.field.provider')}>{appt.provider}</DetailField>}
        {appt.location && <DetailField label={t('detail.field.location')}>{appt.location}</DetailField>}
        {appt.prep_required && appt.prep_notes && (
          <DetailField label={t('detail.field.prep')}>{appt.prep_notes}</DetailField>
        )}
        {appt.transport_needed && <DetailField label={t('detail.field.transport')}>{t('detail.rideNeeded')}</DetailField>}
        {(questionCount ?? 0) > 0 && (
          <DetailField label={t('detail.field.questions')}>
            {t('detail.pendingQuestions', { count: questionCount })}
          </DetailField>
        )}
        {appt.notes && <DetailField label={t('detail.field.notes')}>{appt.notes}</DetailField>}
      </DetailShell>

      <ConfirmActionDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('detail.confirmDelete.title', { title: appt.title })}
        description={t('detail.confirmDelete.description')}
        confirmLabel={t('detail.confirmDelete.confirm')}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete(appt);
        }}
      />
    </>
  );
}
